import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { AdmissionCategoryChangeUtils } from '../utils';

export class FeeHandlers {
    constructor(component) {
        this.component = component;
    }

    // Helper function to get unique fee types with highest totalFee from selectedFeeStructure array
    getUniqueFeeTypes = (selectedFeeStructure) => {
        if (!Array.isArray(selectedFeeStructure)) {
            return [];
        }

        const allFeeTypes = selectedFeeStructure.reduce((acc, category) => {
            if (Array.isArray(category?.feeTypes)) {
                acc.push(...category.feeTypes);
            }
            return acc;
        }, []);

        // Create a map to store unique fee types with highest totalFee
        const feeTypeMap = new Map();
        allFeeTypes.forEach(feeType => {
            if (!feeType?._id) return;

            const existingFeeType = feeTypeMap.get(feeType._id);
            if (!existingFeeType || (feeType.totalFee || 0) > (existingFeeType.totalFee || 0)) {
                feeTypeMap.set(feeType._id, feeType);
            }
        });

        return Array.from(feeTypeMap.values());
    };

    // Helper function to get total fee types count from selectedFeeStructure array
    getTotalFeeTypesCount = (selectedFeeStructure) => {
        return this.getUniqueFeeTypes(selectedFeeStructure).length;
    };

    // Helper function to get combined structure name from selectedFeeStructure array
    getCombinedStructureName = (selectedFeeStructure) => {
        if (!Array.isArray(selectedFeeStructure) || selectedFeeStructure.length === 0) {
            return '';
        }
        return selectedFeeStructure.map(structure => structure.structureName).join(', ');
    };

    // Helper function to get combined category IDs from selectedFeeStructure array
    getCombinedCategoryIds = (selectedFeeStructure) => {
        if (!Array.isArray(selectedFeeStructure) || selectedFeeStructure.length === 0) {
            return [];
        }
        return selectedFeeStructure.map(structure => structure._id);
    };

    handleOneTimeDueDateChange = (value, feeType) => {
        const feeTypeId = feeType._id ?? feeType.feeTypeId;
        const updatedOneTimeFees = this.component.state.oneTimeFees?.map(fee => {
            const feeTypeIdInternal = fee._id ?? fee.feeTypeId;
            if (feeTypeIdInternal === feeTypeId) {
                const installmentId = AdmissionCategoryChangeUtils.getInstallmentIdForDate(
                    value,
                    this.component.state.selectedFeePlan,
                    this.component.state.oneTimeFees
                );
                return {
                    ...fee,
                    dueDate: value,
                    installmentId: installmentId
                };
            }
            return fee;
        });
        this.component.setState({ oneTimeFees: updatedOneTimeFees });
    };

    handleOneTimeFeeChange = (value, feeType) => {
        const feeTypeId = feeType._id ?? feeType.feeTypeId;
        const updatedOneTimeFees = this.component.state.oneTimeFees?.map(fee => {
            const feeTypeIdInternal = fee._id ?? fee.feeTypeId;
            if (feeTypeIdInternal === feeTypeId) {
                return {
                    ...fee,
                    totalFee: Number(value) || 0
                };
            }
            return fee;
        });
        this.component.setState({ oneTimeFees: updatedOneTimeFees });
    };

    handlePercentageChange = (e, installmentIndex, rowData) => {
        try {
            const percentage = Number(e.target.value);
            if (percentage < 0 || percentage > 100) {
                this.component.showToast('warn', 'Warning', 'Percentage must be between 0 and 100');
                return;
            }

            const updatedInstallmentData = [...this.component.state.installmentData];
            const currentInstallment = updatedInstallmentData[installmentIndex];

            if (!currentInstallment || !currentInstallment.feeTypes) {
                console.error('Invalid installment data');
                return;
            }

            const feeTypeIndex = currentInstallment.feeTypes.findIndex(fee =>
                fee.feeType === rowData.feeType &&
                fee.feeTypeId === rowData.feeTypeId
            );

            if (feeTypeIndex === -1) {
                console.error('Fee type not found');
                return;
            }

            const calculatedFee = Math.round((rowData.totalFee * percentage) / 100);

            currentInstallment.feeTypes[feeTypeIndex] = {
                ...currentInstallment.feeTypes[feeTypeIndex],
                percentage: percentage,
                calculatedFee: calculatedFee
            };

            this.component.setState({ installmentData: updatedInstallmentData });
        } catch (error) {
            console.error('Error in handlePercentageChange:', error);
                        this.component.setState({ selectedFeeSchedule: null });
                                    this.component.setState({ selectedFeeSchedule: null });
                                                this.component.setState({ selectedFeeSchedule: null });
                        this.component.showToast('error', 'Error', error.message || 'Failed to check fee structure');
        }
    };

    handleCalculatedFeeChange = (e, installmentIndex, rowData) => {
        try {
            if (rowData.isOneTime) {
                this.component.showToast('warn', 'Warning', 'Cannot modify calculated fee for one-time fees');
                return;
            }

            const newCalculatedFee = Number(e.target.value);
            if (newCalculatedFee < 0 || newCalculatedFee > rowData.totalFee) {
                this.component.showToast('warn', 'Warning', 'Calculated fee cannot exceed total fee');
                return;
            }

            const updatedInstallmentData = [...this.component.state.installmentData];
            const currentInstallment = updatedInstallmentData[installmentIndex];

            if (!currentInstallment || !currentInstallment.feeTypes) {
                console.error('Invalid installment data');
                return;
            }

            const feeTypeIndex = currentInstallment.feeTypes.findIndex(fee =>
                fee.feeType === rowData.feeType &&
                fee.feeTypeId === rowData.feeTypeId
            );

            if (feeTypeIndex === -1) {
                console.error('Fee type not found');
                return;
            }

            const percentage = Math.round((newCalculatedFee * 100) / rowData.totalFee);

            currentInstallment.feeTypes[feeTypeIndex] = {
                ...currentInstallment.feeTypes[feeTypeIndex],
                percentage: percentage,
                calculatedFee: newCalculatedFee
            };

            this.component.setState({ installmentData: updatedInstallmentData });
        } catch (error) {
            console.error('Error in handleCalculatedFeeChange:', error);
            this.component.showToast('error', 'Error', 'Failed to update calculated fee');
        }
    };

    handleTotalFeeChange = (value, feeTypeIndex, feeTypeId, installmentId) => {
        this.component.setState(prevState => {
            // Update selectedFeeStructure array
            const updatedSelectedFeeStructure = prevState.selectedFeeStructure.map(category => {
                const updatedFeeTypes = category.feeTypes.map(fee => {
                    if (fee._id === feeTypeId) {
                        return {
                            ...fee,
                            totalFee: value
                        };
                    }
                    return fee;
                });

                return {
                    ...category,
                    feeTypes: updatedFeeTypes
                };
            });

            const updatedInstallmentData = prevState.installmentData.map(installment => {
                return {
                    ...installment,
                    feeTypes: installment.feeTypes.map(feeType => {
                        if (feeType._id !== feeTypeId) return feeType;
                        return {
                            ...feeType,
                            totalFee: value
                        };
                    })
                };
            });

            return {
                selectedFeeStructure: updatedSelectedFeeStructure,
                installmentData: updatedInstallmentData
            };
        });
    };

    handleClosingAmountChange = (value, rowIndex) => {
        const numValue = parseFloat(value) || 0;

        this.component.setState(prevState => {
            const updatedFeeInfo = [...(prevState.oldFeeInfo?.feeInfo || [])];

            if (updatedFeeInfo[rowIndex]) {
                const currentRow = updatedFeeInfo[rowIndex];
                const amountPaid = parseFloat(currentRow.amountPaid || 0);

                updatedFeeInfo[rowIndex] = {
                    ...currentRow,
                    closingAmount: numValue,
                    closingDueAmt: Math.max(0, numValue - Math.min(numValue, amountPaid)),
                    closingAdvance: (amountPaid || 0) - numValue > 0 ? (amountPaid || 0) - numValue : 0
                };

                const totalClosingDueAmt = updatedFeeInfo?.reduce((sum, item) => sum + (item.closingDueAmt || 0), 0);
                const totalClosingAdvance = updatedFeeInfo?.reduce((sum, item) => sum + (item.closingAdvance || 0), 0);

                return {
                    oldFeeInfo: {
                        ...prevState.oldFeeInfo,
                        feeInfo: updatedFeeInfo,
                        totalClosingDueAmt,
                        totalClosingAdvance
                    }
                };
            }

            return prevState;
        });
    };

    handleFeePlanChange = async () => {
        // feePlanInfo is an array, so we need to get the first element
        // All fee schedules for a student must use the same planId
        const feePlanInfoArray = this.component.state?.newFeeResponse?.feePlanInfo;
        const selectedFeePlan = Array.isArray(feePlanInfoArray) ? feePlanInfoArray[0] : feePlanInfoArray;
        
        if (!selectedFeePlan) {
            return;
        }

        if (this.getTotalFeeTypesCount(this.component.state.selectedFeeStructure) === 0) {
            await this.component.getFeeStructure();
            await this.component.getInstallmentsList();
            await this.component.fetchRelatedFeeSchedules();
        }

        const feeStructureDetailsArr = Array.isArray(this.component.state.newFeeResponse.feeStructureDetails)
            ? this.component.state.newFeeResponse.feeStructureDetails
            : [this.component.state.newFeeResponse.feeStructureDetails];

        let newFeeTypes = feeStructureDetailsArr.reduce((acc, detail) => {
            if (Array.isArray(detail?.stepOne)) {
                acc.push(...detail?.stepOne.map(feeType => feeType?.feeTypeId));
            }
            return acc;
        }, []);

        const feeCategoriesArr = Array.isArray(this.component.state.newFeeResponse.feeCategory)
            ? this.component.state.newFeeResponse.feeCategory
            : [this.component.state.newFeeResponse.feeCategory];

        let allFeeTypes = feeCategoriesArr.reduce((acc, category) => {
            if (Array.isArray(category?.feeTypes)) {
                acc.push(...category.feeTypes);
            }
            return acc;
        }, []);

        const feeTypeMap = new Map();
        allFeeTypes.forEach(fee => {
            if (!feeTypeMap.has(fee._id) || feeTypeMap.get(fee._id).totalFee <= fee.totalFee) {
                feeTypeMap.set(fee._id, fee);
            }
        });
        const uniqueFeeTypesFromMap = Array.from(feeTypeMap.values());

        const oneTimeFees = uniqueFeeTypesFromMap.filter(fee => fee.isOneTime && newFeeTypes.includes(fee._id));
        const regularFees = uniqueFeeTypesFromMap.filter(fee => !fee.isOneTime && newFeeTypes.includes(fee._id));

        const installmentData = selectedFeePlan.installments
            ?.map((installment, index) => ({
                installment: `Installment ${index + 1}`,
                installmentId: installment.installmentId,
                dueDate: new Date(installment.date || Date.now()),
                feeTypes: regularFees?.map(feeType => ({
                    ...feeType,
                    percentage: 0,
                    calculatedFee: 0
                }))
            })) || [];

        const oneTimeFeeSection = oneTimeFees?.map(feeType => ({
            ...feeType,
            percentage: 100,
            calculatedFee: feeType.totalFee
        }));

        // Extract unique fee category IDs from all feeStructureDetails
        let feeCategoryIds = feeStructureDetailsArr.map(detail => detail?.feeCategory).filter(Boolean);
        let uniqueFeeCategoryIds = [...new Set(feeCategoryIds)];

        // Find all matching fee categories
        let selectedFeeCategories = feeCategoriesArr.filter(cat => uniqueFeeCategoryIds.includes(cat._id));

        // If no matching categories found, use the first available category
        if (selectedFeeCategories.length === 0 && feeCategoriesArr.length > 0) {
            selectedFeeCategories = [feeCategoriesArr[0]];
        }

        // Combine fee types from all selected categories
        let allFeeTypesInCategories = selectedFeeCategories.reduce((acc, category) => {
            let feeTypesInCategory = category.feeTypes.filter(feeType => newFeeTypes.includes(feeType._id));
            acc.push(...feeTypesInCategory);
            return acc;
        }, []);

        // Remove duplicates based on _id
        let uniqueFeeTypesInCategories = allFeeTypesInCategories.reduce((acc, feeType) => {
            if (!acc.find(existing => existing._id === feeType._id)) {
                acc.push(feeType);
            }
            return acc;
        }, []);

        // Create selectedFeeStructure as array of categories with their respective fee types
        let selectedFeeStructure = selectedFeeCategories.map(category => {
            let feeTypesInCategory = category.feeTypes.filter(feeType => newFeeTypes.includes(feeType._id));
            return {
                ...category,
                feeTypes: feeTypesInCategory
            };
        });

        // Get all fee types from all categories to find duplicates
        let allCategoryFeeTypes = selectedFeeStructure.reduce((acc, category) => {
            category.feeTypes.forEach(feeType => {
                acc.push({
                    ...feeType,
                    categoryId: category._id,
                    categoryName: category.structureName
                });
            });
            return acc;
        }, []);

        // Create map of unique fee types with highest totalFee
        const uniqueFeeTypeMap = new Map();
        allCategoryFeeTypes.forEach(feeType => {
            if (!feeType._id) return;

            const existingFeeType = uniqueFeeTypeMap.get(feeType._id);
            if (!existingFeeType || (feeType.totalFee || 0) > (existingFeeType.totalFee || 0)) {
                uniqueFeeTypeMap.set(feeType._id, feeType);
            }
        });

        const uniqueFeeTypes = Array.from(uniqueFeeTypeMap.values());

        // Group unique fee types back to their respective categories
        const categoryFeeTypeMap = new Map();
        uniqueFeeTypes.forEach(feeType => {
            if (!categoryFeeTypeMap.has(feeType.categoryId)) {
                categoryFeeTypeMap.set(feeType.categoryId, []);
            }
            categoryFeeTypeMap.get(feeType.categoryId).push(feeType);
        });

        // Rebuild selectedFeeStructure with only unique fee types
        selectedFeeStructure = selectedFeeStructure.map(category => {
            const categoryFeeTypes = categoryFeeTypeMap.get(category._id) || [];
            return {
                ...category,
                feeTypes: categoryFeeTypes
            };
        });

        // Remove empty categories (categories with no fee types after deduplication)
        selectedFeeStructure = selectedFeeStructure.filter(category => category.feeTypes.length > 0);

        this.component.setState({
            selectedFeePlan,
            installmentData,
            oneTimeFees: oneTimeFeeSection,
            selectedFeeStructure: selectedFeeStructure,
            onShowNewFeeInfo: true,
            isActiveAlterFeeSchedules: false,
            isFromFeeScheduleSelection: false,
            isShowInstallmentInfo: true,
        }, async () => {
            if (!selectedFeeStructure.length && !oneTimeFeeSection.length) {
                await this.component.getFeeStructure();
                await this.component.getInstallmentsList();
                await this.component.fetchRelatedFeeSchedules();
            }
        });
    };

    handleSelectedFeeScheduleChange = (selectedFeeScheduleId) => {
        const selectedFeeSchedule = this.component.state.relatedFeeSchedules.find(schedule => schedule._id === selectedFeeScheduleId);
        if (!selectedFeeSchedule) {
            return;
        }

        const selectedFeePlan = this.component.state.plansData?.find(plan => plan._id === selectedFeeSchedule.planId);
        if (!selectedFeePlan) {
            this.component.setState({ selectedFeeSchedule: null });
            this.component.showToast('warn', 'Warning', 'Fee plan information not available for selected schedule');
            return;
        }

        let newFeeTypes = Array.isArray(selectedFeeSchedule.stepOne)
            ? selectedFeeSchedule.stepOne.map(feeType => feeType.feeTypeId)
            : [];

        const filteredFeeCategory = this.component.state.feeStructureInfo.find(category =>
            category._id === selectedFeeSchedule.feeCategory
        );
        if (!filteredFeeCategory) {
            this.component.setState({ selectedFeeSchedule: null });
            this.component.showToast('warn', 'Warning', 'Fee category not found for selected schedule');
            return;
        }

        const oneTimeFees = filteredFeeCategory?.feeTypes?.filter(fee =>
            fee.isOneTime &&
            newFeeTypes.includes(fee._id) &&
            fee.admissionType?.includes(this.component.state.selectedAdmission) &&
            !fee.admissionType?.includes(this.component.state.selectedStudentData.admissionCategory)
        ) || [];

        const regularFees = filteredFeeCategory?.feeTypes?.filter(fee =>
            !fee.isOneTime &&
            newFeeTypes.includes(fee._id) &&
            fee.admissionType?.includes(this.component.state.selectedAdmission) &&
            !fee.admissionType?.includes(this.component.state.selectedStudentData.admissionCategory)
        ) || [];

        const installmentData = selectedFeePlan.installments
            ?.map((installment, index) => ({
                installment: `Installment ${index + 1}`,
                installmentId: installment.installmentId,
                dueDate: new Date(installment.date || Date.now()),
                feeTypes: regularFees?.map(feeType => ({
                    ...feeType,
                    percentage: 0,
                    calculatedFee: 0
                }))
            })) || [];

        const oneTimeFeeSection = oneTimeFees?.map(feeType => ({
            ...feeType,
            percentage: 100,
            calculatedFee: feeType.totalFee
        })) || [];

        let feeTypesInCategories = filteredFeeCategory.feeTypes?.filter(feeType =>
            newFeeTypes.includes(feeType._id) &&
            feeType.admissionType?.includes(this.component.state.selectedAdmission) &&
            !feeType.admissionType?.includes(this.component.state.selectedStudentData.admissionCategory)
        ) || [];

        let selectedFeeStructure = [{
            ...filteredFeeCategory,
            feeTypes: feeTypesInCategories
        }];

        const updatedNewFeeResponse = {
            ...this.component.state.newFeeResponse,
            feeStructureDetails: [selectedFeeSchedule],
            feeCategory: [filteredFeeCategory],
            feePlanInfo: selectedFeePlan
        };

        if (!oneTimeFeeSection?.length && this.getTotalFeeTypesCount(selectedFeeStructure) === 0) {
            this.component.setState({ 
                oneTimeFees: [], 
                selectedFeeStructure: [],
                selectedFeeSchedule: null 
            });
            return this.component.showToast('warn', 'No Fees Available', 'No fee types available for the selected schedule');
        }

        this.component.setState({
            newFeeResponse: updatedNewFeeResponse,
            selectedFeePlan,
            installmentData,
            oneTimeFees: oneTimeFeeSection,
            selectedFeeStructure: selectedFeeStructure,
            onShowNewFeeInfo: true,
            isFromFeeScheduleSelection: true,
            isShowInstallmentInfo: true
        });
    };

    onhandleFee = async () => {
        try {
            const { selectedFeePlan, installmentData, oneTimeFees, selectedFeeStructure } = this.component.state;

            const isValid = AdmissionCategoryChangeUtils.validateInstallmentPercentages(installmentData, this.component.showToast);
            if (!isValid) return;

            // Get fee structure details array
            const feeStructureDetailsArr = Array.isArray(this.component.state.newFeeResponse.feeStructureDetails)
                ? this.component.state.newFeeResponse.feeStructureDetails
                : [this.component.state.newFeeResponse.feeStructureDetails];

            // Create fee schedules array - one for each fee structure
            const feeSchedules = feeStructureDetailsArr.map(feeStructureDetail => {
                // Find the corresponding category for this fee structure
                const correspondingCategory = selectedFeeStructure.find(category =>
                    category._id === feeStructureDetail.feeCategory
                ) || selectedFeeStructure[0]

                // Get fee type IDs for this specific category
                const categoryFeeTypeIds = correspondingCategory.feeTypes.map(feeType => feeType._id);

                // Filter installmentData to only include fee types from this category
                const filteredInstallmentData = installmentData.map(installment => ({
                    ...installment,
                    feeTypes: installment.feeTypes.filter(feeType => 
                        categoryFeeTypeIds.includes(feeType._id || feeType.feeTypeId)
                    )
                })).filter(installment => installment.feeTypes.length > 0); // Remove empty installments

                // Filter oneTimeFees to only include fee types from this category
                const filteredOneTimeFees = oneTimeFees.filter(feeType => 
                    categoryFeeTypeIds.includes(feeType._id || feeType.feeTypeId)
                );

                // Process stepOne data for this specific category
                const categoryStepOne = AdmissionCategoryChangeUtils.processStepOneData([correspondingCategory], filteredOneTimeFees);

                // Process stepTwo data for this specific category with filtered data
                const categoryStepTwo = AdmissionCategoryChangeUtils.processStepTwoData(filteredInstallmentData, filteredOneTimeFees);

                return {
                    feeStructureId: feeStructureDetail._id,
                    feeCategory: correspondingCategory._id,
                    scheduleName: correspondingCategory.structureName,
                    stepOne: categoryStepOne,
                    stepTwo: AdmissionCategoryChangeUtils.sortStepTwoData(categoryStepTwo),
                    academicYear: localStorage.getItem('userAcademicYear'),
                    status: true,
                    planId: this.component.state.selectedFeePlan._id,
                    branchId: this.component.state.selectedStudentData.branchId
                };
            });

            const payload = {
                feeSchedules: feeSchedules
            };

            this.component.raiseChangeRequest(payload);
        } catch (error) {
            this.component.setState({ selectedFeeSchedule: null });
            this.component.showToast('error', 'Error', error.message || 'An error occurred while creating fee schedule');
        }
    };
}
